/**
 * MUTAGIM.SHOP — Product Detail Page (Phase 6)
 * Page produit complète avec galerie, variantes et produits similaires
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Loader2, Star, Truck, Shield, Share2, Bell, Users, ChevronLeft, ChevronRight,
  Check, AlertTriangle, Minus, Plus, Heart, ZoomIn, X
} from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { NetflixRow } from '../components/product/NetflixRow';
import { CompleteLookSection, CustomersAlsoBoughtSection } from '../components/UpsellSection';
import { ProductReviews } from '../components/product/ProductReviews';
import { ProductCategoriesBanner } from '../components/product/ProductCategoriesBanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { useWishlist } from '../contexts/WishlistContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ImageZoomModal } from '../components/ImageZoomModal';
import { useCurrency } from '../contexts/CurrencyContext';
import { useCart } from '../contexts/CartContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Composant Bouton Favoris
function WishlistButton({ productId, productName }) {
  const { toggleWishlist, isInWishlist } = useWishlist();
  const [loading, setLoading] = useState(false);
  const inWishlist = isInWishlist(productId);
  
  const handleClick = async () => {
    setLoading(true);
    await toggleWishlist(productId, productName);
    setLoading(false);
  };
  
  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={cn(
        "p-3 rounded-full transition-all duration-200",
        inWishlist 
          ? "bg-red-500/20 text-red-500 hover:bg-red-500/30" 
          : "bg-zinc-800 text-gray-400 hover:text-red-400 hover:bg-zinc-700"
      )}
      aria-label={inWishlist ? "Retirer des favoris" : "Ajouter aux favoris"}
      data-testid="product-wishlist-btn"
    >
      <Heart className={cn("w-6 h-6", inWishlist && "fill-current")} />
    </button>
  );
}

export function ProductDetailPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { formatPrice, currency } = useCurrency();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedColorVariant, setSelectedColorVariant] = useState(null); // Variante couleur sélectionnée
  const [hoveredVariant, setHoveredVariant] = useState(null); // Pour le zoom au survol
  const [quantity, setQuantity] = useState(1);
  const [showZoom, setShowZoom] = useState(false);
  const [zoomImageUrl, setZoomImageUrl] = useState(null); // URL de l'image à zoomer
  const [zoomContext, setZoomContext] = useState({ type: null, index: 0 }); // Pour la navigation dans le zoom
  const [showStockAlert, setShowStockAlert] = useState(false);
  const [stockAlertEmail, setStockAlertEmail] = useState('');
  const [addingToCart, setAddingToCart] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null); // Taille sélectionnée
  const [viewersCount, setViewersCount] = useState(0); // Compteur de personnes regardant
  const [selectedTextColor, setSelectedTextColor] = useState(null); // Option couleur textuelle sélectionnée
  
  const lang = i18n.language;
  const isRTL = i18n.dir() === 'rtl';

  // Générer un compteur de viewers simulé
  useEffect(() => {
    // Générer un nombre aléatoire entre 8 et 24
    const baseViewers = Math.floor(Math.random() * 17) + 8;
    setViewersCount(baseViewers);
    
    // Variation périodique pour simuler l'activité en temps réel
    const interval = setInterval(() => {
      setViewersCount(prev => {
        const delta = Math.floor(Math.random() * 5) - 2; // -2 à +2
        const newCount = prev + delta;
        return Math.max(5, Math.min(30, newCount)); // Garder entre 5 et 30
      });
    }, 10000); // Toutes les 10 secondes
    
    return () => clearInterval(interval);
  }, [slug]); // Réinitialiser quand le produit change

  // Charger le produit
  const loadProduct = useCallback(async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(`${API_URL}/api/storefront/product/${slug}`);
      setProduct(response.data);
      
      // Sauvegarder dans sessionStorage pour le fil d'Ariane du panier
      sessionStorage.setItem('lastViewedProduct', JSON.stringify({
        slug: slug,
        name: response.data.name?.[lang] || response.data.name?.fr || 'Produit',
        url: `/product/${slug}`
      }));
      
      // Enregistrer la vue
      const sessionId = localStorage.getItem('session_id') || `session_${Date.now()}`;
      localStorage.setItem('session_id', sessionId);
      
      await axios.post(`${API_URL}/api/storefront/products/${response.data.id}/view`, null, {
        params: { session_id: sessionId }
      });
      
      // Sélectionner la première variante disponible
      const variants = response.data.variants || [];
      const availableVariant = variants.find(v => v.available);
      if (availableVariant) {
        setSelectedVariant(availableVariant);
      }
      
      // Auto-sélectionner si une seule taille
      const sizes = response.data.sizes || [];
      if (sizes.length === 1 && variants.length > 0) {
        const singleSizeVariant = variants.find(v => v.attributes?.size === sizes[0]);
        if (singleSizeVariant) {
          setSelectedVariant(singleSizeVariant);
          setSelectedSize(sizes[0]);
        }
      }
      
      // Auto-sélectionner si une seule variante couleur (image)
      const variantImgs = response.data.images?.variants || [];
      if (variantImgs.length === 1) {
        setSelectedColorVariant(0);
      }
      
      // Auto-sélectionner si une seule option couleur textuelle
      const colorOpts = response.data.color_options || [];
      if (colorOpts.length === 1) {
        setSelectedTextColor(0);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching product:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  // Récupérer les images et vidéos
  const getMedia = () => {
    if (!product) return { images: [], qcImages: [], variantImages: [], video: null, hasQc: false };
    const mediaItems = [];
    const qcMediaItems = [];
    const variantMediaItems = [];
    let videoUrl = null;
    let hasQc = false;
    const seenUrls = new Set(); // Pour éviter les doublons
    
    // Helper pour ajouter une image sans doublon
    const addUniqueImage = (url, targetArray = mediaItems) => {
      if (!url) return;
      let normalizedUrl;
      if (url.startsWith('http')) {
        normalizedUrl = url;
      } else if (url.startsWith('/uploads/')) {
        normalizedUrl = `${API_URL}/api${url}`;
      } else if (url.startsWith('/api/')) {
        normalizedUrl = `${API_URL}${url}`;
      } else {
        normalizedUrl = url;
      }
      const urlKey = normalizedUrl.replace(API_URL, ''); // Normaliser pour comparaison
      if (!seenUrls.has(urlKey)) {
        seenUrls.add(urlKey);
        targetArray.push(normalizedUrl);
      }
    };
    
    // Support both formats: array or object with main/qc/gallery
    if (Array.isArray(product.images)) {
      product.images.forEach(img => addUniqueImage(img));
    } else if (product.images) {
      // New format: { main: [], qc: [], variants: [], ai_generated: [], has_qc: bool }
      // First add AI generated images (they should be the main display images)
      if (product.images.ai_generated && Array.isArray(product.images.ai_generated)) {
        product.images.ai_generated.forEach(img => addUniqueImage(img));
      }
      // Then add main images
      if (product.images.main && Array.isArray(product.images.main)) {
        product.images.main.forEach(img => addUniqueImage(img));
      }
      if (product.images.gallery) {
        product.images.gallery.forEach(img => addUniqueImage(img));
      }
      // QC photos
      if (product.images.qc && Array.isArray(product.images.qc)) {
        product.images.qc.forEach(img => {
          let url = img;
          if (!img.startsWith('http')) {
            url = img.startsWith('/uploads/') ? `${API_URL}/api${img}` : `${API_URL}${img}`;
          }
          qcMediaItems.push(url);
        });
      }
      // Variant images (B-1, B-2, etc.) - these go to the gallery AND link to variants
      if (product.images.variants && Array.isArray(product.images.variants)) {
        product.images.variants.forEach(v => {
          variantMediaItems.push(v);
          // Also add to main gallery
          if (v.path) {
            addUniqueImage(v.path);
          }
        });
      }
      hasQc = product.images.has_qc || qcMediaItems.length > 0;
    }
    
    // Ajouter les images AI générées SANS les marquer séparément
    // Elles sont traitées comme des images normales
    if (product.ai_generated_images && Array.isArray(product.ai_generated_images)) {
      product.ai_generated_images.forEach((img) => {
        if (img.url) {
          addUniqueImage(img.url);
        }
      });
    }
    
    // Check for video URL
    if (product.video_url) {
      videoUrl = product.video_url;
    }
    
    return {
      images: mediaItems.length > 0 ? mediaItems : ['/placeholder-product.jpg'],
      qcImages: qcMediaItems,
      variantImages: variantMediaItems,
      video: videoUrl,
      hasQc: hasQc
    };
  };

  const { images, qcImages, variantImages, video: productVideo, hasQc } = getMedia();
  
  // Toutes les images pour la galerie (déjà dédupliquées dans getMedia)
  const allImages = images;

  // Check if a media item is a video
  const isVideo = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
    const lowerUrl = url.toLowerCase();
    return videoExtensions.some(ext => lowerUrl.includes(ext)) ||
           lowerUrl.includes('youtube.com') ||
           lowerUrl.includes('youtu.be') ||
           lowerUrl.includes('vimeo.com');
  };

  // Get YouTube embed URL
  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (ytMatch) {
      return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${ytMatch[1]}`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1`;
    }
    
    // Direct video URL
    return url;
  };

  // Formater l'URL de l'image
  const getImageUrl = (img) => {
    if (!img) return '/placeholder-product.jpg';
    if (img.startsWith('http')) return img;
    if (img.startsWith('/uploads/')) return `${API_URL}/api${img}`;
    if (img.startsWith('/api/')) return `${API_URL}${img}`;
    return img;
  };

  // Synchroniser la sélection de couleur quand on change d'image
  const syncColorVariantFromImage = (imageIndex) => {
    if (variantImages.length === 0) return;
    
    const currentImageUrl = allImages[imageIndex];
    // Trouver si cette image correspond à une variante couleur
    const variantIdx = variantImages.findIndex(v => {
      const variantUrl = v.path?.startsWith('http') ? v.path : `${API_URL}${v.path}`;
      return currentImageUrl === variantUrl || currentImageUrl === v.path;
    });
    
    if (variantIdx >= 0) {
      setSelectedColorVariant(variantIdx);
    }
  };

  // Ouvrir le zoom avec une image spécifique
  const openZoom = (imageUrl) => {
    setZoomImageUrl(getImageUrl(imageUrl));
    setZoomContext({ type: 'single', index: 0 });
    setShowZoom(true);
  };

  // Ouvrir le zoom avec navigation (pour QC et variantes)
  const openZoomWithNavigation = (imageUrl, type, index) => {
    setZoomImageUrl(getImageUrl(imageUrl));
    setZoomContext({ type, index });
    setShowZoom(true);
  };

  // Naviguer dans le zoom
  const navigateZoom = (direction) => {
    let newIndex;
    let imagesList;
    
    if (zoomContext.type === 'qc') {
      imagesList = qcImages;
    } else if (zoomContext.type === 'variants') {
      imagesList = variantImages.map(v => v.path);
    } else if (zoomContext.type === 'main') {
      imagesList = allImages;
    } else {
      return;
    }
    
    if (direction === 'prev') {
      newIndex = zoomContext.index === 0 ? imagesList.length - 1 : zoomContext.index - 1;
    } else {
      newIndex = zoomContext.index === imagesList.length - 1 ? 0 : zoomContext.index + 1;
    }
    
    const newImage = imagesList[newIndex];
    setZoomImageUrl(getImageUrl(newImage));
    setZoomContext({ ...zoomContext, index: newIndex });
  };

  // Obtenir les variantes groupées par attribut
  const getVariantsByAttribute = (attribute) => {
    if (!product?.variants) return [];
    const values = new Set();
    product.variants.forEach(v => {
      if (v.attributes?.[attribute]) {
        values.add(v.attributes[attribute]);
      }
    });
    return Array.from(values);
  };

  // Sélectionner une variante
  const selectVariant = (attribute, value) => {
    const variant = product.variants.find(v => 
      v.attributes?.[attribute] === value
    );
    if (variant) {
      setSelectedVariant(variant);
    }
  };

  // Ajouter au panier
  const handleAddToCart = async () => {
    if (!product) return;
    
    // Vérifier si une taille est sélectionnée (si des tailles existent)
    const hasSizes = sizes.length > 0;
    const hasColorVariants = variantImages.length > 0;
    // Les options couleur textuelles ne sont utilisées QUE si pas de variantes image
    const hasTextColors = !hasColorVariants && (product?.color_options || []).length > 0;
    
    if (hasSizes && !selectedVariant) {
      toast.error(t('product.selectSize', 'Veuillez sélectionner une taille'));
      return;
    }
    
    if (hasColorVariants && selectedColorVariant === null) {
      toast.error(t('product.selectColor', 'Veuillez sélectionner une couleur'));
      return;
    }
    
    if (hasTextColors && selectedTextColor === null) {
      toast.error(t('product.selectColor', 'Veuillez sélectionner une couleur'));
      return;
    }
    
    if (hasTextColors && selectedTextColor === null) {
      toast.error(t('product.selectColor', 'Veuillez sélectionner une couleur'));
      return;
    }
    
    setAddingToCart(true);
    try {
      // Passer l'ID de la variante, pas le SKU
      const variantId = selectedVariant?.id || selectedVariant?._id || null;
      
      // Inclure la couleur sélectionnée dans les métadonnées
      let colorLabel = null;
      let variantImageUrl = null;
      
      if (hasColorVariants && variantImages[selectedColorVariant]) {
        colorLabel = variantImages[selectedColorVariant].label;
        // Récupérer l'URL de l'image de la variante sélectionnée
        variantImageUrl = variantImages[selectedColorVariant].path;
      } else if (hasTextColors && selectedTextColor !== null) {
        colorLabel = product.color_options[selectedTextColor];
      }
      
      await addToCart(product.id, quantity, variantId, { 
        color: colorLabel,
        variant_image: variantImageUrl 
      });
      toast.success(t('product.addedToCart', 'Produit ajouté au panier'));
    } catch (err) {
      toast.error(t('product.addError', 'Erreur lors de l\'ajout'));
    } finally {
      setAddingToCart(false);
    }
  };

  // Créer une alerte de stock
  const handleStockAlert = async () => {
    if (!stockAlertEmail) {
      toast.error(t('product.enterEmail', 'Veuillez entrer votre email'));
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/storefront/stock-alert`, {
        product_id: product.id,
        email: stockAlertEmail,
        variant_sku: selectedVariant?.sku
      });
      
      toast.success(t('product.alertCreated', 'Vous serez notifié(e) dès que ce produit sera disponible'));
      setShowStockAlert(false);
      setStockAlertEmail('');
    } catch (err) {
      toast.error(t('product.alertError', 'Erreur lors de la création de l\'alerte'));
    }
  };

  // Partager le produit
  const handleShare = async () => {
    const url = window.location.href;
    const text = `${product?.name?.[lang] || 'Produit'} - Mutagim.shop`;
    
    // Essayer l'API Web Share
    if (navigator.share) {
      try {
        await navigator.share({ title: text, url });
        return;
      } catch (err) {
        // Fallback
      }
    }
    
    // Copier le lien
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('product.linkCopied', 'Lien copié !'));
    } catch (err) {
      toast.error(t('product.copyError', 'Impossible de copier le lien'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-mutagim-dark">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-10 w-10 animate-spin text-mutagim-red" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-mutagim-dark">
        <Header />
        <div className="text-center py-32">
          <p className="text-red-500 mb-4">{error || t('product.notFound', 'Produit non trouvé')}</p>
          <Link to="/" className="text-mutagim-red hover:underline">
            {t('common.backHome', 'Retour à l\'accueil')}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const name = product.name?.[lang] || product.name?.fr || 'Produit';
  const description = product.description?.[lang] || product.description?.fr || '';
  const isOutOfStock = product.out_of_stock || product.total_stock === 0;
  
  // Détecter si c'est un accessoire (pas besoin de taille)
  const accessoryTags = ['accessoire', 'accessory', 'bonnet', 'beanie', 'chapeau', 'hat', 'casquette', 'cap', 
                         'lunettes', 'glasses', 'écharpe', 'scarf', 'gants', 'gloves', 'ceinture', 'belt',
                         'sac', 'bag', 'portefeuille', 'wallet', 'bijou', 'jewelry', 'montre', 'watch',
                         'collier', 'necklace', 'bracelet', 'bague', 'ring', 'boucles', 'earrings',
                         'porte-clés', 'keychain', 'bandana', 'headband', 'bandeau'];
  const productTags = (product.tags || []).map(t => t.toLowerCase());
  const productName = (product.name?.fr || product.name?.en || '').toLowerCase();
  
  const isAccessory = accessoryTags.some(tag => 
    productTags.some(pt => pt.includes(tag)) || productName.includes(tag)
  ) || product.is_one_size === true;
  
  // Pour les accessoires, ne pas afficher de sélecteur de taille
  const rawSizes = getVariantsByAttribute('size');
  const sizes = isAccessory ? [] : rawSizes;
  const colors = getVariantsByAttribute('color');

  return (
    <div className="min-h-screen bg-mutagim-dark overflow-x-hidden" data-testid="product-detail-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8 overflow-x-hidden">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-400 mb-4">
          <Link to="/" className="hover:text-white">{t('nav.home', 'Accueil')}</Link>
          <span className="mx-2">/</span>
          <span className="text-white">{name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 relative">
          {/* COLONNE GAUCHE: Galerie + QC */}
          <div className="space-y-4">
            {/* Image/Vidéo principale */}
            <div 
              className="relative aspect-square bg-gray-900 rounded-lg overflow-hidden cursor-zoom-in group"
              onClick={() => selectedImage < allImages.length && openZoomWithNavigation(allImages[selectedImage], 'main', selectedImage)}
            >
              {/* Afficher la vidéo si sélectionnée */}
              {selectedImage === allImages.length && productVideo ? (
                <div className="w-full h-full">
                  {productVideo.includes('youtube') || productVideo.includes('vimeo') ? (
                    <iframe
                      src={getVideoEmbedUrl(productVideo)}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                      title="Product video"
                    />
                  ) : (
                    <video
                      src={productVideo.startsWith('/api/') ? `${API_URL}${productVideo}` : productVideo}
                      className="w-full h-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                    />
                  )}
                </div>
              ) : (
                <img
                  src={getImageUrl(allImages[selectedImage])}
                  alt={name}
                  className="w-full h-full object-cover scale-110 transition-transform duration-300 group-hover:scale-120"
                />
              )}
              
              {/* Zoom icon */}
              <div className="absolute top-4 end-4 p-2 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="w-5 h-5 text-white" />
              </div>
              
              {/* Badge */}
              {product.badges?.primary_badge && (
                <Badge 
                  className={cn(
                    "absolute top-4 start-4",
                    product.badges.primary_badge === 'almost_sold_out' && "bg-red-600",
                    product.badges.primary_badge === 'trending' && "bg-orange-500",
                    product.badges.primary_badge === 'new' && "bg-green-500",
                    product.badges.primary_badge === 'mutagim_pick' && "bg-purple-500"
                  )}
                >
                  {product.badges.primary_badge === 'almost_sold_out' && t('badges.almostSoldOut')}
                  {product.badges.primary_badge === 'trending' && t('badges.trending')}
                  {product.badges.primary_badge === 'new' && t('badges.new')}
                  {product.badges.primary_badge === 'mutagim_pick' && t('badges.mutagimPick')}
                </Badge>
              )}
              
              {/* Navigation arrows */}
              {allImages.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute start-2 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newIndex = selectedImage === 0 ? allImages.length - 1 : selectedImage - 1;
                      setSelectedImage(newIndex);
                      syncColorVariantFromImage(newIndex);
                    }}
                  >
                    {isRTL ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute end-2 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newIndex = selectedImage === allImages.length - 1 ? 0 : selectedImage + 1;
                      setSelectedImage(newIndex);
                      syncColorVariantFromImage(newIndex);
                    }}
                  >
                    {isRTL ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                  </Button>
                </>
              )}
            </div>
            
            {/* VIGNETTES GALERIE - Sous l'image principale */}
            {allImages.length > 1 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium">
                  {allImages.length} photos
                </p>
                <div className="grid grid-cols-6 gap-1.5">
                  {allImages.slice(0, 12).map((img, idx) => (
                    <button
                      key={`gallery-thumb-${idx}`}
                      className={cn(
                        "aspect-square rounded-lg overflow-hidden border-2 transition-all",
                        selectedImage === idx 
                          ? "border-mutagim-red ring-2 ring-mutagim-red/50" 
                          : "border-zinc-700 hover:border-zinc-500"
                      )}
                      onClick={() => setSelectedImage(idx)}
                      data-testid={`gallery-thumbnail-${idx}`}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`Photo ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </button>
                  ))}
                  {allImages.length > 12 && (
                    <button
                      className="aspect-square rounded-lg overflow-hidden border-2 border-zinc-700 hover:border-zinc-500 bg-zinc-800 flex items-center justify-center text-gray-400 text-xs font-medium"
                      onClick={() => openZoomWithNavigation(allImages[12], 'main', 12)}
                    >
                      +{allImages.length - 12}
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {/* VARIANTES COULEUR - Sous l'image principale */}
            {variantImages.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-gray-400 font-medium">
                  {variantImages.length} {variantImages.length > 1 ? t('product.colorsAvailable', 'couleurs disponibles') : t('product.colorAvailable', 'couleur disponible')}
                  {selectedColorVariant !== null && (
                    <span className="text-purple-400 ml-2">— {variantImages[selectedColorVariant]?.label}</span>
                  )}
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {variantImages.map((variant, idx) => (
                    <button
                      key={`variant-${idx}`}
                      className={cn(
                        "flex flex-col items-center gap-1 p-1 rounded-lg transition-all",
                        selectedColorVariant === idx 
                          ? "ring-2 ring-purple-500 bg-purple-500/10" 
                          : "hover:bg-gray-800"
                      )}
                      onClick={() => {
                        setSelectedColorVariant(idx);
                        // Trouver l'index de cette image dans allImages et y aller
                        const variantUrl = variant.path?.startsWith('http') ? variant.path : `${API_URL}${variant.path}`;
                        const imageIdx = allImages.findIndex(img => img === variantUrl || img === variant.path);
                        if (imageIdx >= 0) {
                          setSelectedImage(imageIdx);
                        }
                      }}
                      title={variant.label || `Couleur ${idx + 1}`}
                    >
                      <div className={cn(
                        "w-full aspect-square rounded-lg overflow-hidden border-2 transition-all",
                        selectedColorVariant === idx 
                          ? "border-purple-500" 
                          : "border-gray-700"
                      )}>
                      <img
                          src={getImageUrl(variant.path)}
                          alt={variant.label || `Variante ${idx + 1}`}
                          className="w-full h-full object-cover scale-110"
                        />
                      </div>
                      <span className={cn(
                        "text-[9px] w-full truncate text-center",
                        selectedColorVariant === idx ? "text-purple-400" : "text-gray-500"
                      )}>
                        {variant.label || `C${idx + 1}`}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Section QC Photos - Sous la galerie dans la colonne gauche */}
            {hasQc && qcImages.length > 0 && (
              <div data-testid="qc-photos-section-left" className="pt-2">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-green-600 text-white px-2 py-0.5 text-xs">
                    <Check className="w-3 h-3 mr-1" />
                    QC
                  </Badge>
                  <span className="text-sm font-medium text-white">Photos Qualité ({qcImages.length})</span>
                </div>
                
                <div className="grid grid-cols-5 gap-1.5">
                  {qcImages.map((qcImg, idx) => (
                    <button
                      key={`qc-left-${idx}`}
                      className="aspect-square rounded overflow-hidden border border-green-600/40 hover:border-green-500 transition-all relative group"
                      onClick={() => openZoomWithNavigation(qcImg, 'qc', idx)}
                      data-testid={`qc-photo-left-${idx}`}
                    >
                      <img
                        src={getImageUrl(qcImg)}
                        alt={`QC Photo ${idx + 1}`}
                        className="w-full h-full object-cover scale-110 group-hover:scale-125 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                        <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="absolute top-0.5 right-0.5 bg-green-600 text-white text-[8px] px-1 rounded">
                        {idx + 1}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* COLONNE DROITE: Informations produit - SCROLLABLE */}
          <div className="space-y-6">
            {/* Nom */}
            <div className="flex items-start justify-between gap-4">
              <h1 className="text-2xl md:text-3xl font-bold text-white flex-1">
                {name}
              </h1>
              {/* Bouton Favoris */}
              <WishlistButton productId={product.id} productName={name} />
            </div>
            
            {/* Note et avis */}
            {product.stats?.average_rating > 0 && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={cn(
                        "h-5 w-5",
                        star <= Math.floor(product.stats.average_rating)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-600"
                      )}
                    />
                  ))}
                </div>
                <span className="text-gray-400">
                  {product.stats.average_rating.toFixed(1)} ({product.stats.reviews_count} {t('product.reviews', 'avis')})
                </span>
              </div>
            )}
            
            {/* Compteur viewers - toujours affiché */}
            <div className="flex items-center gap-2 text-green-400 text-sm bg-green-500/10 px-3 py-2 rounded-lg w-fit">
              <Users className="w-4 h-4" />
              <span>{viewersCount} {t('product.viewingNow', 'personnes regardent ce produit')}</span>
            </div>
            
            {/* Prix */}
            <div className="space-y-1">
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold text-mutagim-red">
                  {formatPrice(product.pricing?.prices)}
                </span>
                {product.pricing?.compare_at_prices && (
                  <span className="text-xl text-gray-500 line-through">
                    {formatPrice(product.pricing.compare_at_prices)}
                  </span>
                )}
              </div>
            </div>
            
            {/* Livraison gratuite */}
            <div className="flex items-center gap-2 text-green-400 bg-green-500/10 px-4 py-3 rounded-lg">
              <Check className="w-5 h-5" />
              <div>
                <p className="font-medium">{t('product.freeShipping', 'Livraison gratuite — incluse dans le prix')}</p>
                <p className="text-sm text-gray-400">
                  {product.delivery?.delay_message || t('product.deliveryTime', 'Livraison estimée: 10-20 jours')}
                </p>
              </div>
            </div>
            
            <Separator className="bg-gray-800" />
            
            {/* Sélecteur de taille */}
            {sizes.length > 0 && (
              <div className="space-y-3">
                <label className="text-white font-medium">
                  {t('product.size', 'Taille')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((size) => {
                    const variant = product.variants.find(v => v.attributes?.size === size);
                    const isSelected = selectedVariant?.attributes?.size === size;
                    const isAvailable = variant?.available;
                    
                    return (
                      <button
                        key={size}
                        className={cn(
                          "px-4 py-2 rounded-lg border-2 transition-all text-sm font-medium",
                          isSelected 
                            ? "border-mutagim-red bg-mutagim-red/20 text-white" 
                            : isAvailable
                              ? "border-gray-700 text-gray-300 hover:border-gray-500"
                              : "border-gray-800 text-gray-600 line-through cursor-not-allowed"
                        )}
                        onClick={() => isAvailable && selectVariant('size', size)}
                        disabled={!isAvailable}
                      >
                        {size}
                        {variant?.low_stock && (
                          <span className="block text-xs text-orange-400">
                            {variant.low_stock_message}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Badge Taille Unique pour les accessoires */}
            {isAccessory && sizes.length === 0 && (
              <div className="space-y-3">
                <label className="text-white font-medium">
                  {t('product.size', 'Taille')}
                </label>
                <Badge className="bg-green-600/20 text-green-400 border border-green-600/50 px-4 py-2">
                  {t('product.oneSize', 'Taille unique')}
                </Badge>
              </div>
            )}
            
            {/* Sélecteur de couleur */}
            {colors.length > 0 && (
              <div className="space-y-3">
                <label className="text-white font-medium">
                  {t('product.color', 'Couleur')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {colors.map((color) => {
                    const variant = product.variants.find(v => v.attributes?.color === color);
                    const isSelected = selectedVariant?.attributes?.color === color;
                    const isAvailable = variant?.available;
                    
                    return (
                      <button
                        key={color}
                        className={cn(
                          "px-4 py-2 rounded-lg border-2 transition-all text-sm",
                          isSelected 
                            ? "border-mutagim-red bg-mutagim-red/20 text-white" 
                            : isAvailable
                              ? "border-gray-700 text-gray-300 hover:border-gray-500"
                              : "border-gray-800 text-gray-600 line-through cursor-not-allowed"
                        )}
                        onClick={() => isAvailable && selectVariant('color', color)}
                        disabled={!isAvailable}
                      >
                        {color}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Quantité */}
            <div className="space-y-3">
              <label className="text-white font-medium">
                {t('product.quantity', 'Quantité')}
              </label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="border-gray-700 text-white"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  disabled={isOutOfStock}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-white font-medium w-12 text-center">
                  {quantity}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="border-gray-700 text-white"
                  onClick={() => setQuantity(q => q + 1)}
                  disabled={isOutOfStock}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex gap-3">
              {isOutOfStock ? (
                <Button
                  className="flex-1 bg-gray-700 hover:bg-gray-600"
                  onClick={() => setShowStockAlert(true)}
                  data-testid="notify-stock-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  {t('product.notifyWhenAvailable', 'M\'avertir du retour en stock')}
                </Button>
              ) : (
                <Button
                  className={cn(
                    "flex-1",
                    (sizes.length > 0 && !selectedVariant) || 
                    (variantImages.length > 0 && selectedColorVariant === null) ||
                    (product?.color_options?.length > 0 && variantImages.length === 0 && selectedTextColor === null)
                      ? "bg-gray-600 hover:bg-gray-500"
                      : "bg-mutagim-red hover:bg-red-700"
                  )}
                  onClick={handleAddToCart}
                  disabled={addingToCart}
                  data-testid="add-to-cart-btn"
                >
                  {addingToCart ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  {(sizes.length > 0 && !selectedVariant) || 
                   (variantImages.length > 0 && selectedColorVariant === null) ||
                   (product?.color_options?.length > 0 && variantImages.length === 0 && selectedTextColor === null)
                    ? t('product.selectOptions', 'Sélectionnez vos options')
                    : t('product.addToCart', 'Ajouter au panier')
                  }
                </Button>
              )}
              
              <Button
                variant="outline"
                size="icon"
                className="border-gray-700 text-white"
                onClick={handleShare}
                data-testid="share-btn"
              >
                <Share2 className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Garanties */}
            <div className="grid grid-cols-2 gap-3 pt-4">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Truck className="w-4 h-4" />
                <span>{t('product.freeDelivery', 'Livraison gratuite')}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Shield className="w-4 h-4" />
                <span>{t('product.securePayment', 'Paiement sécurisé')}</span>
              </div>
            </div>
            
            {/* Logos de paiement */}
            <div className="flex items-center gap-3 pt-2">
              <span className="text-gray-500 text-xs">{t('product.acceptedPayments', 'Paiements acceptés')}:</span>
              <div className="flex items-center gap-2">
                {/* Visa - Logo texte simple */}
                <div className="bg-[#1A1F71] rounded px-2 py-1">
                  <span className="text-white text-xs font-bold italic">VISA</span>
                </div>
                {/* Mastercard */}
                <div className="flex items-center bg-white rounded px-1.5 py-0.5">
                  <div className="w-4 h-4 bg-[#EB001B] rounded-full -mr-1.5"></div>
                  <div className="w-4 h-4 bg-[#F79E1B] rounded-full"></div>
                </div>
                {/* American Express */}
                <div className="bg-[#016FD0] rounded px-2 py-1">
                  <span className="text-white text-[10px] font-bold">AMEX</span>
                </div>
                {/* PayPal */}
                <div className="bg-[#003087] rounded px-2 py-1">
                  <span className="text-white text-[10px] font-bold">Pay<span className="text-[#009CDE]">Pal</span></span>
                </div>
                {/* Apple Pay */}
                <div className="bg-black rounded px-2 py-1 flex items-center gap-0.5">
                  <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <span className="text-white text-[10px] font-medium">Pay</span>
                </div>
                {/* Bit */}
                <div className="bg-gradient-to-r from-[#00D4AA] to-[#00B4D8] rounded px-2 py-0.5">
                  <span className="text-white text-xs font-bold">bit</span>
                </div>
              </div>
            </div>
            
            <Separator className="bg-gray-800" />
            
            {/* Description - Max 500 caractères */}
            {description && (
              <div className="space-y-2">
                <h3 className="text-white font-medium">
                  {t('product.description', 'Description')}
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm">
                  {description.length > 500 ? `${description.substring(0, 500)}...` : description}
                </p>
              </div>
            )}

            {/* Section Options Couleur Textuelles (sans images) */}
            {product?.color_options?.length > 0 && variantImages.length === 0 && (
              <div data-testid="text-color-options-section" className="pt-2">
                <div className="flex items-center gap-3 mb-2">
                  <Badge className="bg-purple-600 text-white px-2 py-0.5 text-xs">
                    {product.color_options.length} couleurs
                  </Badge>
                  <h3 className="text-base font-semibold text-white">
                    {t('product.color', 'Couleur')}
                  </h3>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {product.color_options.map((color, idx) => (
                    <button
                      key={`text-color-${idx}`}
                      className={cn(
                        "px-4 py-2 rounded-lg border-2 transition-all text-sm font-medium",
                        selectedTextColor === idx 
                          ? "border-purple-500 bg-purple-500/20 text-white ring-2 ring-purple-500/50" 
                          : "border-gray-700 bg-gray-800/40 text-gray-300 hover:border-purple-400 hover:text-white"
                      )}
                      onClick={() => setSelectedTextColor(idx)}
                      data-testid={`text-color-${color}`}
                    >
                      {color}
                      {selectedTextColor === idx && (
                        <Check className="inline-block w-4 h-4 ml-2 text-purple-400" />
                      )}
                    </button>
                  ))}
                </div>
                
                {selectedTextColor === null && (
                  <p className="text-orange-400 text-xs mt-2">* Sélectionnez une couleur</p>
                )}
              </div>
            )}

            {/* Supprimer l'ancienne section QC de la colonne droite - maintenant dans la colonne gauche */}
          </div>
        </div>
        
        {/* Section Avis Clients */}
        <ProductReviews 
          productSlug={slug} 
          productStats={product.stats} 
        />
        
        {/* Upsell & Cross-sell Sections (Phase 15) */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Complète le look - Produits complémentaires */}
          <CompleteLookSection productId={product.id} />
          
          {/* Les clients ont aussi acheté - Cross-sell */}
          <CustomersAlsoBoughtSection productId={product.id} />
        </div>
        
        {/* Produits similaires */}
        {product.similar_products?.length > 0 && (
          <div className="mt-16">
            <NetflixRow 
              row={{
                id: 'similar',
                title: { 
                  fr: 'Produits similaires', 
                  he: 'מוצרים דומים', 
                  en: 'Similar Products' 
                },
                emoji: '✨',
                products: product.similar_products
              }}
            />
          </div>
        )}
        
        {/* Bandeau des catégories */}
        <ProductCategoriesBanner />
      </main>
      
      <Footer />
      
      {/* Modal Zoom avec navigation et contrôles de zoom */}
      <ImageZoomModal
        open={showZoom}
        onOpenChange={(open) => {
          setShowZoom(open);
          if (!open) {
            setZoomImageUrl(null);
            setZoomContext({ type: null, index: 0 });
          }
        }}
        imageUrl={zoomImageUrl || getImageUrl(allImages[selectedImage])}
        alt={name}
        showNavigation={zoomContext.type && zoomContext.type !== 'single'}
        onPrev={() => navigateZoom('prev')}
        onNext={() => navigateZoom('next')}
        currentIndex={zoomContext.index}
        totalImages={
          zoomContext.type === 'qc' ? qcImages.length :
          zoomContext.type === 'variants' ? variantImages.length :
          allImages.length
        }
      />
      
      {/* Modal Alerte Stock */}
      <Dialog open={showStockAlert} onOpenChange={setShowStockAlert}>
        <DialogContent className="bg-mutagim-dark border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              {t('product.notifyTitle', 'M\'avertir du retour en stock')}
            </DialogTitle>
            <DialogDescription>
              {t('product.notifyDescription', 'Entrez votre email pour être notifié(e) dès que ce produit sera de nouveau disponible.')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="email"
              placeholder={t('product.emailPlaceholder', 'Votre email')}
              value={stockAlertEmail}
              onChange={(e) => setStockAlertEmail(e.target.value)}
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStockAlert(false)}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button onClick={handleStockAlert} className="bg-mutagim-red hover:bg-red-700">
              {t('product.notifyMe', 'Me notifier')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProductDetailPage;
